import React from 'react'
import ReactDOM from 'react-dom'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import Autosuggest from 'react-autosuggest'
import qs from 'qs'
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'
import find from 'lodash/find'
import Icon from '../misc/Icons'
import SearchProviderContainer from '../../containers/search/SearchProviderContainer'
import { useSearchContext } from '../../services/search/SearchContext'
import { useSupplementarySearch } from '../../services/search/SupplementarySearchContext'
import { getDocumentType } from '../../utils/search/getDocumentType'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getRepoforId } from '../../utils/getRepoforId'
import { isCurrentUser } from '../../utils/isCurrentUser'
import { setLastLocation } from '../routing/LastLocationProvider'
import { getLinkForRepo } from '../../utils/getLinkForRepo'
import QuestionCountBadge from '../uiKit/QuestionCountBadge'
import { newSet } from '../../utils/newItem'
import { showLiveViewModal } from '../../utils/liveView/showLiveViewModal'
import { showNewRepoModal, showNewSectionModal } from '../../actions/modals'
import { updatePreviewItem } from '../../actions/previewItem'
import { STATIC_QUICK_ACTIONS, toQuickActionHit } from '../../services/search/local/quickActionsRegistry'
import { STATIC_NAV_ENTRIES, toNavigationHit } from '../../services/search/local/navigationRegistry'
import { buildHelpSearchUrl, EXTERNAL_LINKS, toExternalLinkHit } from '../../services/search/local/externalLinksRegistry'
import { localProviderId } from '../../services/search/presentationEngine'
import {
  getRecentSearches,
  recordRecentSearch,
  removeRecentSearch,
  searchRecentSearches,
  RECENT_SEARCHES_PREFILL_COUNT,
} from '../../utils/search/recentSearches'
import SearchPreviewPane, { shouldShowPreviewFor } from './SearchPreviewPane'
import {
  trackNavigationUse,
  trackQuickActionUse,
  trackHelpUse,
  trackContentUse,
} from '../../services/search/newSearchAnalytics'

// Resolve the local set/question id a suggestion points at, for the lightweight
// preview modal (plain Enter). Returns null for anything that isn't a local set
// or question (packs/folders/nav/quick-actions/external have no modal preview).
function getPreviewItemIdForSuggestion(suggestion) {
  if (!suggestion) return null
  if (suggestion.__recent) {
    const kind = suggestion.__recentItemType
    if ((kind === 'set' || kind === 'question') && suggestion.item) return suggestion.item.id
    return null
  }
  const kind = getDocumentType(suggestion)
  if (kind === 'set' || kind === 'question') return suggestion.id || suggestion._id
  return null
}

// Human-readable label for a section header.
function getSectionLabel(index) {
  const labels = {
    repos: 'Content',
    folders: 'Content',
    // eslint-disable-next-line camelcase
    sets_questions: 'Content',
    'Recent Searches': 'Recent Searches',
    'Quick Actions': 'Quick Actions',
    Recent: 'Recent Content',
    Navigation: 'Navigation',
    'Help & External': 'Help',
  }
  return labels[index] || index
}

// Library-type section indexes. The first one encountered gets the "Library"
// header; subsequent ones use a blank header (no label, no border flicker).
const LIBRARY_INDEXES = new Set(['repos', 'folders', 'sets_questions'])

function renderSectionTitle(section) {
  if (!section.index) return null
  // `showHeader` is pre-computed in buildAllSections (see below) so
  // renderSectionTitle never has to carry mutable state between calls.
  if (!section.showHeader) return null
  const label = getSectionLabel(section.index)
  // No leading icon on category-header rows — the grey background + dark label
  // (see SearchDropdown.scss) is the visual distinction from result rows.
  return (
    <div className='searchDropdown-sectionHeader'>
      <span className='searchDropdown-sectionHeader-label'>{label}</span>
    </div>
  )
}

// ─── Recent helpers ────────────────────────────────────────────────────────────
// Resolve `state.recentActivities` (sorted by activityTime desc) into hit-shaped
// rows by joining against `state.sets` / `state.questions`. Items that haven't
// hydrated yet are skipped (they'll appear once the data loads).
function buildRecentSuggestions(recentActivities, sets, questions, limit = 5) {
  if (!Array.isArray(recentActivities) || recentActivities.length === 0) return []
  const ordered = sortBy(recentActivities, 'activityTime').reverse()
  const hits = []
  for (let i = 0; i < ordered.length && hits.length < limit; i += 1) {
    const activity = ordered[i]
    if (activity && activity.item) {
      let item = null
      if (activity.itemType === 'set') {
        item = find(sets, { id: activity.item })
      } else if (activity.itemType === 'question') {
        item = find(questions, { id: activity.item })
      }
      if (item) {
        hits.push({
          id: `recent-${activity.itemType}-${item.id}`,
          objectID: `recent-${activity.itemType}-${item.id}`,
          __recent: true,
          __recentItemType: activity.itemType,
          item,
        })
      }
    }
  }
  return hits
}

// Build the recent-search rows (content-only query history) for the empty state.
function buildRecentSearchHits(recentSearches) {
  if (!Array.isArray(recentSearches)) return []
  return recentSearches.map((query) => ({
    id: `recentSearch-${query}`,
    objectID: `recentSearch-${query}`,
    __recentSearch: true,
    query,
  }))
}

// Empty-state sections — shown when the input is focused with no query.
// Pure function of static config + Redux-derived recent items + the persisted
// recent-search query history. Order: Recent Searches → Quick Actions → Recent.
function buildEmptyStateSections(recentSearches, recentSuggestions) {
  const sections = []
  // Show only the most recent few in the empty state; the full history is kept
  // and made searchable while typing (see SearchAutocomplete).
  const recentSearchHits = buildRecentSearchHits(
    (Array.isArray(recentSearches) ? recentSearches : []).slice(0, RECENT_SEARCHES_PREFILL_COUNT),
  )
  if (recentSearchHits.length > 0) {
    sections.push({ index: 'Recent Searches', showHeader: true, hits: recentSearchHits })
  }
  // Prefill lists — gathered from each registry by the explicit `prefill` flag.
  // Quick actions and navigation get their own headers; external entries (none
  // flagged today) fold into the Quick Actions group.
  const quickActionHits = [
    ...STATIC_QUICK_ACTIONS.filter((a) => a.prefill).map(toQuickActionHit),
    ...EXTERNAL_LINKS.filter((e) => e.prefill).map(toExternalLinkHit),
  ]
  if (quickActionHits.length > 0) {
    sections.push({ index: 'Quick Actions', showHeader: true, hits: quickActionHits })
  }
  const navHits = STATIC_NAV_ENTRIES.filter((e) => e.prefill).map(toNavigationHit)
  if (navHits.length > 0) {
    sections.push({ index: 'Navigation', showHeader: true, hits: navHits })
  }
  if (recentSuggestions.length > 0) {
    sections.push({ index: 'Recent Content', showHeader: true, hits: recentSuggestions })
  }
  return sections
}

// Merge recent-search matches + Algolia sections + supplementary sections into
// a flat list, ordering:
//   1. Recent Searches (matches while typing)  2. Quick Actions  3. Navigation
//   4. Library  5. See all results  6. Help & External  7. Search Help Center
// Empty sections are stripped first (prevents orphan dividers).
// Library-type sections get showHeader:true only on the first occurrence.
function buildAllSections(
  suggestions, additiveResults, currentRefinement, recentMatchHits, engine,
) {
  const additive = Array.isArray(additiveResults) ? additiveResults : []
  const hasLibraryResults = suggestions.some((s) => s.hits && s.hits.length > 0)

  const head = []

  // ── Recent search matches (shown first while typing) ───────────────────────
  if (Array.isArray(recentMatchHits) && recentMatchHits.length > 0) {
    head.push({ index: 'Recent Searches', hits: recentMatchHits })
  }

  // ── Quick Actions ──────────────────────────────────────────────────────────
  const qaBlock = additive.find((r) => r.providerId === localProviderId('quick-actions', engine))
  if (qaBlock && Array.isArray(qaBlock.hits) && qaBlock.hits.length > 0) {
    // Cap quick-action results when library content is also present to avoid burying it.
    const qaHits = hasLibraryResults ? qaBlock.hits.slice(0, 2) : qaBlock.hits
    head.push({ index: 'Quick Actions', hits: qaHits })
  }

  // ── Navigation ─────────────────────────────────────────────────────────────
  const navBlock = additive.find((r) => r.providerId === localProviderId('navigation', engine))
  if (navBlock && Array.isArray(navBlock.hits) && navBlock.hits.length > 0) {
    // Cap navigation results when library content is present.
    const navHits = hasLibraryResults ? navBlock.hits.slice(0, 3) : navBlock.hits
    head.push({ index: 'Navigation', hits: navHits })
  }

  // ── Library sections: Algolia only (local content engines run as shadows) ──
  const algoliaLibrary = suggestions.filter((s) => s.hits && s.hits.length > 0)

  const extBlock = additive.find((r) => r.providerId === localProviderId('external-help', engine))
  const hasExtHits = extBlock && Array.isArray(extBlock.hits) && extBlock.hits.length > 0

  const merged = [...head, ...algoliaLibrary]

  let libraryHeaderShown = false
  const withHeaders = merged.map((section) => {
    if (LIBRARY_INDEXES.has(section.index)) {
      if (!libraryHeaderShown) {
        libraryHeaderShown = true
        return { ...section, showHeader: true }
      }
      return { ...section, showHeader: false }
    }
    return { ...section, showHeader: true }
  })

  // Footer rows appended after the header pass.
  const footers = []
  // "See all results for …" — directly under the library item list.
  if (currentRefinement) {
    footers.push({ hits: [{ showMore: currentRefinement }], index: '', showHeader: false })
  }
  // Help & External results (matched links only — the Search Help Center row is
  // a separate, always-last footer below).
  if (hasExtHits) {
    footers.push({ index: 'Help & External', hits: extBlock.hits.slice(), showHeader: true })
  }
  // "Search Help Center" deep-link — always the last thing in the dropdown when
  // there is a query (under help results if present, otherwise just last).
  if (currentRefinement) {
    footers.push({
      index: '',
      showHeader: false,
      hits: [{ __helpShowMore: true, query: currentRefinement, objectID: 'help-show-more' }],
    })
  }

  return withHeaders.concat(footers)
}

// Mouse handlers for previewable rows (set/question/recent set/question). Like
// the recent-search rows, these stop the click from reaching react-autosuggest
// (which would select + collapse the dropdown), so single vs double click can
// be told apart by the parent: single click opens the preview modal, double
// click opens the item.
function previewClickHandlers(suggestion, onClick, onDoubleClick) {
  return {
    onMouseDown: (e) => { e.preventDefault(); e.stopPropagation() },
    onClick: (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (onClick) onClick(suggestion)
    },
    onDoubleClick: (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (onDoubleClick) onDoubleClick(suggestion)
    },
  }
}

// Render a recent-list hit. Sets get the badge icon with count; questions use
// the search-question icon. The resolved item is on suggestion.item.
function renderRecentSuggestion(suggestion, { onPreviewItemClick, onPreviewItemDoubleClick } = {}) {
  const { item, __recentItemType: kind } = suggestion
  if (!item) return null
  const isSet = kind === 'set'
  const label = isSet ? item.name : getQuestionBodyLabel(item)
  const handlers = previewClickHandlers(suggestion, onPreviewItemClick, onPreviewItemDoubleClick)
  return (
    <div
      className={`searchDropdownItem searchDropdownItem--recent searchDropdownItem--${isSet ? 'set' : 'question'}`}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...handlers}
    >
      <div className='searchDropdownItem-leadingIcon'>
        {isSet ? (
          <QuestionCountBadge
            color='blueLight'
            size='medium'
            questionCount={Array.isArray(item.questions) ? item.questions.length : 0}
          />
        ) : (
          <QuestionCountBadge color='blueLight' size='medium' questionCount='–' />
        )}
      </div>
      <div className='searchDropdownItem-title'>
        {label}
      </div>
    </div>
  )
}

const renderSuggestion = (suggestion, {
  onDeleteRecentSearch, onRecentSearchClick, onPreviewItemClick, onPreviewItemDoubleClick,
  onQuickActionClick, onQuickActionMouseDown,
} = {}) => {
  let inSharedRepo = false
  let inPublicConsumerRepo = false
  let inPublicAuthorRepo = false
  if (suggestion.repo) {
    const containingRepo = getRepoforId(suggestion.repo)
    if (containingRepo) {
      inSharedRepo = containingRepo.shared
      inPublicConsumerRepo = containingRepo.role === 'consumer'
      inPublicAuthorRepo = containingRepo.role === 'author'
    }
  }
  if (suggestion.createdBy) {
    if (suggestion.published) {
      if (isCurrentUser(suggestion.author)) {
        inPublicAuthorRepo = true
      } else {
        inPublicConsumerRepo = true
      }
    }
  }

  // Recent search — a previously-run content query. Selecting it re-fills the
  // input and runs the search again (handled in SearchAutocomplete).
  if (suggestion.__recentSearch) {
    return (
      <div
        className='searchDropdownItem searchDropdownItem--recentSearch'
        // Keep the input focused and stop react-autosuggest from selecting +
        // closing the dropdown on click; we handle the fill ourselves so the
        // search runs and the dropdown stays open.
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onRecentSearchClick) onRecentSearchClick(e, suggestion.query)
        }}
      >
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='magnifying-glass' />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.query}
        </div>
        <button
          type='button'
          className='searchDropdownItem-removeBtn'
          aria-label='Remove recent search'
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (onDeleteRecentSearch) onDeleteRecentSearch(suggestion.query)
          }}
        >
          <Icon name='close-small' />
        </button>
      </div>
    )
  }

  // Quick Action — single-row create affordance. preventDefault on mousedown
  // keeps the input from blurring before the click selects, so a modal-opening
  // quick action can preserve the dropdown query (see openHighlightedSuggestion).
  if (suggestion.__quickAction) {
    return (
      <div
        className='searchDropdownItem searchDropdownItem--quickAction'
        // Keep the input focused and stop react-autosuggest from selecting +
        // collapsing the dropdown on click. react-autosuggest tears the
        // suggestions container down whenever it handles a selection, so a
        // modal-opening quick action (New Pack / New Class) would lose the
        // dropdown that should sit behind the modal. We run the action via our
        // own click handler instead (mirrors the recent-search row).
        //
        // Marking the pending modal on mousedown (earlier than the click) is
        // what keeps the dropdown alive: react-autosuggest has
        // focusInputOnSuggestionClick={false}, so a blur fires around the click
        // and would wipe the query in onBlur before openHighlightedSuggestion
        // sets the flag. The keyboard path has no such blur (Enter keeps focus),
        // which is why it already worked.
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onQuickActionMouseDown) onQuickActionMouseDown(suggestion)
        }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onQuickActionClick) onQuickActionClick(suggestion)
        }}
      >
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name={suggestion.icon || 'plus'} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
      </div>
    )
  }

  // Recent item — surfaces last-touched sets/questions
  if (suggestion.__recent) {
    return renderRecentSuggestion(suggestion, { onPreviewItemClick, onPreviewItemDoubleClick })
  }

  // Help "See all results for …" deep-link to help.plickers.com
  if (suggestion.__helpShowMore) {
    return (
      <div className='searchDropdownItem searchDropdownItem--showMore searchDropdownItem--helpShowMore'>
        <div className='searchDropdownItem-title'>
          {'Search Help Center'}
          {suggestion.query && (
            <React.Fragment>
              {' for '}
              <strong>{suggestion.query}</strong>
            </React.Fragment>
          )}
        </div>
        <div className='searchDropdownItem-trailingIcon'>
          <Icon name='open-link' />
        </div>
      </div>
    )
  }

  // Navigation entry — use class color dot for class entries, compass for others
  if (suggestion.__nav) {
    const isClassEntry = suggestion.color != null
    return (
      <div className='searchDropdownItem searchDropdownItem--nav'>
        <div className='searchDropdownItem-leadingIcon'>
          {isClassEntry ? (
            <div className={`sectionMarker is--colorOnly${suggestion.color ? ` has--classcolor-${suggestion.color}` : ' no--color'} searchDropdownItem-classDot`} />
          ) : (
            <Icon name='compass' />
          )}
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
      </div>
    )
  }

  // External / help link — web icon leading, open-link trailing
  if (suggestion.__external) {
    return (
      <div className='searchDropdownItem searchDropdownItem--external'>
        <div className='searchDropdownItem-leadingIcon'>
          {/* Help-center articles carry their own icon (e.g. info-circle);
              plain external links fall back to the globe. */}
          <Icon name={suggestion.icon && suggestion.icon !== 'external-link' ? suggestion.icon : 'web'} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.label}
        </div>
        <div className='searchDropdownItem-trailingIcon'>
          <Icon name='open-link' />
        </div>
      </div>
    )
  }

  // "See all results for …" footer row
  if (suggestion.showMore) {
    return (
      <div className='searchDropdownItem searchDropdownItem--showMore'>
        <div className='searchDropdownItem-title'>
          See all content results for
          {' '}
          <strong>{suggestion.showMore}</strong>
        </div>
      </div>
    )
  }

  // Pack / repo
  if (suggestion.createdBy) {
    return (
      <div className='searchDropdownItem searchDropdownItem--repo'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name='search-pack' />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.name}
          {suggestion.shared && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
          {inPublicAuthorRepo && <Icon name='globe-shared-author' />}
        </div>
      </div>
    )
  }

  // Folder
  if (suggestion.ancestors) {
    return (
      <div className='searchDropdownItem searchDropdownItem--folder'>
        <div className='searchDropdownItem-leadingIcon'>
          <Icon name={!suggestion.readOnly ? 'folder' : 'folder-isbe'} />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.name}
          {inSharedRepo && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
        </div>
      </div>
    )
  }

  // Set (has questions array) — badge shows count
  if (suggestion.questions) {
    const handlers = previewClickHandlers(suggestion, onPreviewItemClick, onPreviewItemDoubleClick)
    return (
      <div
        className='searchDropdownItem searchDropdownItem--set'
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...handlers}
      >
        <div className='searchDropdownItem-leadingIcon'>
          <QuestionCountBadge
            color='blueLight'
            size='medium'
            questionCount={Array.isArray(suggestion.questions) ? suggestion.questions.length : 0}
          />
        </div>
        <div className='searchDropdownItem-title'>
          {suggestion.name}
          {inSharedRepo && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
        </div>
      </div>
    )
  }

  // Question (has choices array)
  if (suggestion.choices) {
    const handlers = previewClickHandlers(suggestion, onPreviewItemClick, onPreviewItemDoubleClick)
    return (
      <div
        className='searchDropdownItem searchDropdownItem--question'
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...handlers}
      >
        <div className='searchDropdownItem-leadingIcon'>
          <QuestionCountBadge color='blueLight' size='medium' questionCount='–' />
        </div>
        <div className='searchDropdownItem-title'>
          {getQuestionBodyLabel(suggestion)}
          {inSharedRepo && <Icon name='shared' />}
          {inPublicConsumerRepo && <Icon name='globe-shared' />}
        </div>
      </div>
    )
  }

  return null
}

// Platform-aware modifier label so the Cmd glyph renders on Mac and "Ctrl"
// everywhere else, wherever a cmd-based shortcut is shown.
const IS_MAC = typeof navigator !== 'undefined' &&
  navigator.platform.toUpperCase().indexOf('MAC') >= 0
// Cmd/Ctrl+Enter label shown in the input hint. Cmd/Ctrl+Enter always submits
// the entered string to the full results page ("Find content").
const SUBMIT_SHORTCUT_LABEL = IS_MAC ? '\u2318 Enter' : 'Ctrl+Enter'
// "Open search" hint shown when the input is unfocused + empty, to advertise the
// keyboard shortcut that focuses the search field from anywhere.
const SEARCH_SHORTCUT_LABEL = IS_MAC ? '\u2318K' : 'Ctrl+K'

// Window for telling a single click from the first click of a double click on a
// previewable dropdown row. Matches the results-page Hit timing.
const SUGGESTION_CLICK_DELAY = 220

// Bottom-of-dropdown shortcut line, tailored to the highlighted row. Returns an
// ordered list of { keys, label } hints. Always begins with ↑↓ navigate and
// never advertises ⌘K or ⌘+Enter (the input itself now carries those hints: ⌘K
// when unfocused/empty, ⌘Enter "Submit" when there's a typed query). Items
// without a preview ("Enter open"/"run"/"go"/"search") omit the preview/open
// hints.
// Stable identity for a dropdown row. Drives the bottom shortcut hint and the
// persisted-highlight class (item retained across modals), so it must be stable
// across re-renders AND unique per visible row (no two rows share a key).
// Most rows carry an objectID; the footers and registry rows fall back to other
// stable fields.
function shortcutHintKeyFor(s) {
  if (!s) return 'none'
  if (s.objectID != null) return `id:${s.objectID}`
  if (s.showMore != null) return `showMore:${s.showMore}`
  if (s.__helpShowMore) return 'helpShowMore'
  if (s.id != null) return `id:${s.id}`
  if (s._id != null) return `id:${s._id}`
  if (s.path != null) return `path:${s.path}`
  if (s.url != null) return `url:${s.url}`
  if (s.query != null) return `q:${s.query}`
  if (s.label != null) return `label:${s.label}`
  return 'unknown'
}

function getDropdownShortcutHints(highlightedSuggestion) {
  const hints = [{ keys: '↑↓', label: 'navigate' }]
  const s = highlightedSuggestion

  if (!s || s.showMore || s.__helpShowMore) {
    hints.push({ keys: 'Enter', label: 'all results' })
    return hints
  }
  if (s.__recentSearch) {
    hints.push({ keys: 'Enter', label: 'search' })
    return hints
  }
  if (s.__quickAction) {
    hints.push({ keys: 'Enter', label: 'open' })
    return hints
  }
  if (s.__nav) {
    hints.push({ keys: 'Enter', label: 'go' })
    return hints
  }
  if (s.__external) {
    hints.push({ keys: 'Enter', label: 'open' })
    return hints
  }
  if (shouldShowPreviewFor(s)) {
    // First Enter opens the preview; a second Enter (inside the preview) opens
    // the item — so "open" advertises two Enter keys.
    hints.push({ keys: 'Enter', label: 'preview' })
    hints.push({ keys: ['Enter', 'Enter'], label: 'open' })
    return hints
  }
  // Remaining library entities without a preview (pack/folder).
  if (getDocumentType(s)) {
    hints.push({ keys: 'Enter', label: 'open' })
    return hints
  }
  hints.push({ keys: 'Enter', label: 'all results' })
  return hints
}

// The right-aligned input hint switches on state (value clears on blur, so an
// unfocused input is always empty):
//   - empty + unfocused                       → "⌘K" (focus search from anywhere)
//   - empty (focused or navigating dropdown)   → nothing (no query to find yet)
//   - typed query + a dropdown row highlighted → "Find content ⌘Enter"
//   - typed query, no row highlighted          → "Find content Enter"
// The ⌘Enter hint is hidden while the query is empty (e.g. navigating the
// empty-state dropdown); the Cmd/Ctrl+Enter behaviour itself is unchanged —
// see onSuggestionSelected.
const renderInputComponent = (inputProps, hasActiveHighlight, isInputFocused) => {
  const hasValue = !!inputProps.value
  let hint = null
  if (!hasValue && !isInputFocused) {
    hint = (
      <span className='topNav-search-shortcutHint' aria-hidden='true'>
        <kbd className='topNav-search-shortcutHint-key'>{SEARCH_SHORTCUT_LABEL}</kbd>
      </span>
    )
  } else if (hasValue && hasActiveHighlight) {
    hint = (
      <span className='topNav-search-shortcutHint' aria-hidden='true'>
        Search content
        <kbd className='topNav-search-shortcutHint-key'>{SUBMIT_SHORTCUT_LABEL}</kbd>
      </span>
    )
  } else if (hasValue) {
    hint = (
      <span className='topNav-search-shortcutHint' aria-hidden='true'>
        Search content
        <kbd className='topNav-search-shortcutHint-key'>Enter</kbd>
      </span>
    )
  }
  return (
    <React.Fragment>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <input {...inputProps} className='topNav-search' id='topNavSearchBar' />
      {hint}
    </React.Fragment>
  )
}

function SearchAutocomplete({
  getSuggestionValue,
  inputProps,
  onSuggestionSelected,
  onSuggestionHighlighted,
  onRecentSearchSelected,
  onRecentSearchFill,
  onDeleteRecentSearch,
  onSuggestionPreviewClick,
  onSuggestionPreviewDoubleClick,
  onQuickActionClick,
  onQuickActionMouseDown,
  markEnterHandled,
  registerRefine,
  getAwaitingQuickActionModal,
  getDropdownClosedByCreate,
  recentSearches,
  recentSuggestions,
  highlightedSuggestion,
  shortcutHintSuggestion,
  keepOpenBehindModal,
  hasActiveHighlight,
  isInputFocused,
}) {
  const { suggestions, refine, currentRefinement } = useSearchContext()
  const { additiveResults, presentationEngine } = useSupplementarySearch()

  const trimmedQuery = (currentRefinement || '').trim()
  const isEmptyQuery = trimmedQuery.length === 0

  // Expose `refine` to the connected parent's document capture handler (used to
  // fill the input when a recent search is selected via the keyboard).
  React.useEffect(() => {
    if (registerRefine) registerRefine(refine)
  }, [refine, registerRefine])

  // Fuzzy-match the user's query against their full recent-search history and
  // surface the hits as the first dropdown section. Drop an exact match of the
  // current query so we don't show a redundant row.
  const recentMatchHits = isEmptyQuery ?
    [] :
    buildRecentSearchHits(
      searchRecentSearches(currentRefinement)
        .filter((q) => q.toLowerCase() !== trimmedQuery.toLowerCase()),
    )

  const allSections = isEmptyQuery ?
    buildEmptyStateSections(recentSearches, recentSuggestions) :
    buildAllSections(
      suggestions, additiveResults, currentRefinement, recentMatchHits, presentationEngine,
    )

  // Selecting a recent-search row: plain Enter / click fills the input with the
  // term and runs the search (keeping the dropdown open); Cmd/Ctrl+Enter goes
  // to the full results page. (Plain keyboard Enter is intercepted earlier in
  // the parent's capture handler so the dropdown stays open; this covers clicks
  // and the modifier case.)
  const handleSuggestionSelected = (event, data) => {
    const { suggestion, method } = data
    // Tell the parent that react-autosuggest handled this Enter via a real
    // highlighted row, so its input-level onKeyDown doesn't ALSO navigate to
    // the results page (which would double-handle / override this selection).
    if (method === 'enter' && markEnterHandled) markEnterHandled()
    if (suggestion && suggestion.__recentSearch) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault()
      if (event && (event.ctrlKey || event.metaKey)) {
        // Cmd/Ctrl+Enter ("Submit") searches the entered string, not the
        // highlighted recent-search row. With no typed query (it can't happen
        // for a recent-search MATCH, which only appears with a query) fall back
        // to running the recent search itself.
        if (!isEmptyQuery) {
          onSuggestionSelected(event, data)
          return
        }
        if (onRecentSearchSelected) onRecentSearchSelected(suggestion.query)
        return
      }
      refine(suggestion.query)
      if (onRecentSearchFill) onRecentSearchFill(suggestion.query)
      return
    }
    onSuggestionSelected(event, data)
  }

  // Mouse-click on a recent-search row. react-autosuggest's own click handler
  // would select + close the dropdown (and clear results), so the row stops its
  // own click/mousedown from reaching autosuggest (see renderSuggestion) and
  // calls this instead: plain click fills the input + runs the search (dropdown
  // stays open); Cmd/Ctrl+click goes to the full results page.
  const handleRecentSearchClick = (event, query) => {
    if (event && (event.metaKey || event.ctrlKey)) {
      if (onRecentSearchSelected) onRecentSearchSelected(query)
      return
    }
    if (onRecentSearchFill) onRecentSearchFill(query)
  }

  // The persisted highlight survives modal open/close (preview / New Class /
  // New Pack), unlike react-autosuggest's own highlight which is cleared on the
  // redux re-render or the input blur. Driven by the last selected row tracked
  // in shortcutHintSuggestion (keyboard arrows OR mouse hover).
  const persistedKey = shortcutHintKeyFor(shortcutHintSuggestion)
  const hasPersisted = persistedKey !== 'none' && persistedKey !== 'unknown'

  const renderSuggestionWithHandlers = (suggestion) => {
    const el = renderSuggestion(suggestion, {
      onDeleteRecentSearch,
      onRecentSearchClick: handleRecentSearchClick,
      onPreviewItemClick: onSuggestionPreviewClick,
      onPreviewItemDoubleClick: onSuggestionPreviewDoubleClick,
      onQuickActionClick,
      onQuickActionMouseDown,
    })
    if (!el || !hasPersisted) return el
    if (shortcutHintKeyFor(suggestion) !== persistedKey) return el
    const prevClass = (el.props && el.props.className) || ''
    return React.cloneElement(el, { className: `${prevClass} searchDropdownItem--persisted` })
  }

  // Inlined so the preview pane can close over highlightedSuggestion.
  // tabIndex={-1} keeps focus on the input while the user hovers the pane.
  // The preview column is only mounted when the highlighted suggestion is a
  // library entity (set/question/pack/folder, or a recent row that resolves
  // to one of those). Non-library hits don't get a preview, so the dropdown
  // shell falls back to its original single-column width.
  const showPreviewColumn = shouldShowPreviewFor(highlightedSuggestion)
  const renderSuggestionsContainer = ({ containerProps, children }) => {
    if (!children) return undefined
    const shellClass = showPreviewColumn ?
      'searchDropdown searchDropdown--withPreview' :
      'searchDropdown'
    // While the user is just typing (no row highlighted) the input field already
    // shows the bare "Enter" hint, so drop Enter hints from the bottom line to
    // avoid doubling it. When navigating / hovering a row, show the full
    // contextual hints (per-type action) as before.
    const allShortcutHints = getDropdownShortcutHints(shortcutHintSuggestion)
    const shortcutHints = hasActiveHighlight ?
      allShortcutHints :
      allShortcutHints.filter((hint) => {
        const keys = Array.isArray(hint.keys) ? hint.keys : [hint.keys]
        return !keys.includes('Enter')
      })
    return (
      <React.Fragment>
        {/* Dim the page behind the dropdown when a preview card is shown so the
            two-column dropdown reads clearly over a busy content page. Portaled
            to <body> and kept below the top-nav (z-index 500) so the nav, the
            search input and the dropdown itself all stay bright above it. */}
        {showPreviewColumn && typeof document !== 'undefined' && ReactDOM.createPortal(
          <div className='searchDropdown-backdrop' aria-hidden='true' />,
          document.body,
        )}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <div {...containerProps} className={shellClass}>
          <div className='searchDropdown-suggestionsColumn'>
            {children}
            <div className='searchDropdown-shortcutHelp'>
              {shortcutHints.map((hint) => {
                const keyList = Array.isArray(hint.keys) ? hint.keys : [hint.keys]
                return (
                  <span className='searchDropdown-shortcutHelp-item' key={keyList.join('+') + hint.label}>
                    <span className='searchDropdown-shortcutHelp-keys'>
                      {keyList.map((k, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <kbd key={`${k}-${i}`}>{k}</kbd>
                      ))}
                    </span>
                    {hint.label}
                  </span>
                )
              })}
            </div>
          </div>
          {showPreviewColumn && (
            <div
              className='searchDropdown-previewColumn'
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
            >
              <SearchPreviewPane suggestion={highlightedSuggestion} query={currentRefinement} />
              <div className='searchDropdown-previewColumn-fade' aria-hidden='true' />
            </div>
          )}
        </div>
      </React.Fragment>
    )
  }

  return (
    <Autosuggest
      suggestions={allSections}
      multiSection
      onSuggestionsFetchRequested={({ value: v }) => refine(v)}
      onSuggestionsClearRequested={() => {
        // Don't wipe the query when a quick-action modal steals focus — keep the
        // live results visible behind the modal (parity with the preview modal).
        if (getAwaitingQuickActionModal && getAwaitingQuickActionModal()) return
        refine('')
      }}
      getSuggestionValue={getSuggestionValue}
      renderInputComponent={(ip) => renderInputComponent(ip, hasActiveHighlight, isInputFocused)}
      onSuggestionSelected={handleSuggestionSelected}
      renderSuggestionsContainer={renderSuggestionsContainer}
      renderSuggestion={renderSuggestionWithHandlers}
      inputProps={inputProps}
      renderSectionTitle={renderSectionTitle}
      getSectionSuggestions={(section) => section.hits}
      focusInputOnSuggestionClick={false}
      onSuggestionHighlighted={onSuggestionHighlighted}
      shouldRenderSuggestions={() => !(getDropdownClosedByCreate && getDropdownClosedByCreate())}
      // Keep the dropdown rendered behind a quick-action modal (New Pack / New
      // Class). The modal autofocuses its own field, blurring the search input;
      // without this react-autosuggest would collapse the dropdown on that blur.
      // The query is preserved (see onSuggestionsClearRequested), so the live
      // results stay visible — matching the preview modal.
      alwaysRenderSuggestions={keepOpenBehindModal}
    />
  )
}

class SearchBar extends React.Component {
  constructor(props) {
    super(props)
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this)
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this)
    this.onSuggestionPreviewClick = this.onSuggestionPreviewClick.bind(this)
    this.onSuggestionPreviewDoubleClick = this.onSuggestionPreviewDoubleClick.bind(this)
    this.onQuickActionClick = this.onQuickActionClick.bind(this)
    this.onQuickActionMouseDown = this.onQuickActionMouseDown.bind(this)
    this.trackHighlight = this.trackHighlight.bind(this)
    this.setPreviewHighlight = this.setPreviewHighlight.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.getSuggestionValue = this.getSuggestionValue.bind(this)
    this.handleCaptureKeyDown = this.handleCaptureKeyDown.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onRecentSearchSelected = this.onRecentSearchSelected.bind(this)
    this.onRecentSearchFill = this.onRecentSearchFill.bind(this)
    this.onDeleteRecentSearch = this.onDeleteRecentSearch.bind(this)
    this.markEnterHandled = this.markEnterHandled.bind(this)
    this.registerRefine = this.registerRefine.bind(this)
    this.getAwaitingQuickActionModal = this.getAwaitingQuickActionModal.bind(this)
    this.getDropdownClosedByCreate = this.getDropdownClosedByCreate.bind(this)
    this.onQuickActionCreated = this.onQuickActionCreated.bind(this)
    // Real-time highlighted row, kept on the instance (not state) so the Enter
    // capture-handler never acts on a stale row — unlike the debounced preview
    // highlight below.
    this.currentHighlight = null
    // Pending single-click timer for previewable rows (see onSuggestionPreviewClick).
    this.suggestionClickTimer = null
    // Set while a quick-action modal (New Pack / New Class) launched from the
    // dropdown is open, so the dropdown query is preserved (not wiped on the
    // modal's focus-steal) and restored on Esc/cancel — see onBlur,
    // componentDidUpdate and onQuickActionCreated.
    this.awaitingQuickActionModal = false
    this.awaitingQuickActionModalType = null
    // Set on a confirmed create so the deferred restore in componentDidUpdate
    // skips reopening the dropdown (New Class fires HIDE_MODAL before onCreated).
    this.suppressQuickActionRestore = false
    // Set on a confirmed create to force the dropdown shut. While the quick-action
    // modal is open the dropdown is kept rendered behind it (alwaysRenderSuggestions),
    // which leaves react-autosuggest's internal "focused" flag set; when the modal
    // closes on success that stale flag would otherwise keep the dropdown open
    // (showing the empty/prefill state). This gates shouldRenderSuggestions so the
    // list stays collapsed until the user genuinely re-focuses or types again.
    this.dropdownClosedByCreate = false
    // Set by react-autosuggest's onSuggestionSelected (method='enter') which
    // fires BEFORE our input-level onKeyDown. Lets onKeyDown tell apart "Enter
    // acted on a real highlighted row" from "Enter with no real selection →
    // go to the full results page". Reset by onKeyDown after each Enter.
    this.enterHandledBySuggestion = false
    // Stable key of the row currently driving the bottom shortcut hint. Footer
    // rows (e.g. "See all content results") are recreated on every render with
    // no stable objectID, so we guard the hint setState on this key rather than
    // object identity to avoid an infinite render loop.
    this.shortcutHintKey = null
    // Debounce ONLY the preview-pane highlight so fast arrow-scrolling doesn't
    // thrash the preview render; the action highlight (trackHighlight) stays
    // synchronous.
    this.setPreviewHighlight = debounce(this.setPreviewHighlight, 100)
    this.state = {
      value: '',
      highlightedSuggestion: null,
      // Whether a dropdown row is currently highlighted (arrow nav / hover) —
      // drives the right-aligned input hint (Submit ⌘Enter vs a bare Enter;
      // see renderInputComponent).
      hasActiveHighlight: false,
      // The truly-highlighted row (including the "See all content results" /
      // "Search Help Center" footers) used to drive the bottom shortcut hint.
      // Tracked separately from highlightedSuggestion because the preview-pane
      // highlight intentionally skips those footers (see setPreviewHighlight),
      // which would otherwise leave the bottom hint showing the previous row.
      shortcutHintSuggestion: null,
      // Whether the search input currently holds DOM focus — drives the input
      // hint's empty state (⌘K when unfocused, ⌘Enter/nothing when focused; see
      // renderInputComponent).
      isInputFocused: false,
      // Persisted recent-search query history (localStorage is not reactive, so
      // we snapshot it on mount and refresh on focus).
      recentSearches: getRecentSearches(),
    }
  }

  componentDidMount() {
    // Capture phase so this runs BEFORE react-autosuggest's own keydown handler
    // (React 16 binds at document in the bubble phase). Lets us open the preview
    // on plain Enter without autosuggest closing the dropdown or clearing state,
    // so the dropdown is preserved at the same position when the modal closes.
    document.addEventListener('keydown', this.handleCaptureKeyDown, true)
  }

  componentDidUpdate(prevProps) {
    // A quick-action modal we launched from the dropdown has closed. If it
    // closed without a confirmed create (Esc/cancel/background — onCreated would
    // have cleared the flag), restore the dropdown for the preserved query by
    // re-focusing the input.
    if (
      this.awaitingQuickActionModal &&
      prevProps.currentModal === this.awaitingQuickActionModalType &&
      this.props.currentModal !== this.awaitingQuickActionModalType
    ) {
      this.awaitingQuickActionModal = false
      this.awaitingQuickActionModalType = null
      if (typeof document !== 'undefined') {
        // Defer the restore to a macrotask. This serves two purposes:
        //   1. Some modals (New Class is a redux-form with several fields) move
        //      focus to <body> as they unmount AFTER this lifecycle runs; the
        //      deferred re-focus runs once that settles and reliably reopens it.
        //   2. Lets a confirmed-create signal arrive first. New Class's
        //      `newSection` action dispatches HIDE_MODAL (this transition)
        //      BEFORE onQuickActionCreated runs, so the flag isn't cleared yet
        //      here. onQuickActionCreated runs as a microtask (before this
        //      macrotask) and sets suppressQuickActionRestore, so a successful
        //      create won't be mistaken for a cancel and won't reopen.
        window.setTimeout(() => {
          if (this.suppressQuickActionRestore) {
            this.suppressQuickActionRestore = false
            return
          }
          const input = document.getElementById('topNavSearchBar')
          if (input && typeof input.focus === 'function') input.focus()
        }, 0)
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleCaptureKeyDown, true)
    if (this.suggestionClickTimer) {
      clearTimeout(this.suggestionClickTimer)
      this.suggestionClickTimer = null
    }
  }

  // Capture-phase Enter handling for the highlighted row. Runs BEFORE
  // react-autosuggest's own Enter handler so we can keep the dropdown open.
  // Plain Enter is the default action here:
  //   - recent-search row → fill the input with the term + run the search
  //   - set/question row   → open the lightweight preview modal
  // Cmd/Ctrl+Enter (all results) is left to react-autosuggest's
  // onSuggestionSelected.
  handleCaptureKeyDown(e) {
    // Once the preview modal is open it owns the keyboard (its own capture
    // listener swallows keys); do nothing here.
    if (this.props.previewItem) return
    // While an IME (e.g. Japanese) is composing, Enter confirms the candidate
    // (kanji) — keyCode 229 / isComposing. Let the browser handle it; don't
    // treat it as a search/preview Enter.
    if (e.isComposing || e.keyCode === 229) return
    if (e.key !== 'Enter' && e.keyCode !== 13) return
    if (e.shiftKey || e.ctrlKey || e.metaKey) return
    if (!e.target || e.target.id !== 'topNavSearchBar') return
    // Use the synchronous highlight (not the debounced preview state) so Enter
    // always acts on the row that's actually highlighted right now.
    const suggestion = this.currentHighlight
    if (!suggestion) return

    // "See all results" footer → don't intercept; let react-autosuggest handle
    // Enter so it navigates to the full results page (instead of opening the
    // last real row's preview).
    if (suggestion.showMore) return

    // Recent-search row → fill the input and run the query, keeping the
    // dropdown open (stop autosuggest from selecting + closing).
    if (suggestion.__recentSearch) {
      e.preventDefault()
      e.stopImmediatePropagation()
      this.fillRecentSearch(suggestion.query)
      return
    }

    // Previewable set/question → open the lightweight preview modal.
    const previewId = getPreviewItemIdForSuggestion(suggestion)
    if (!previewId) return
    // Stop autosuggest from handling this Enter (which would close the dropdown
    // and clear the highlight) and open the lightweight preview instead.
    e.preventDefault()
    e.stopImmediatePropagation()
    // Opening a preview is a content search — record the query.
    recordRecentSearch(this.state.value)
    this.props.updatePreviewItem(previewId, 'searchDropdown')
  }

  // Stash the SearchContext `refine` fn (provided by SearchAutocomplete) so the
  // capture handler above can run a query when filling a recent search.
  registerRefine(refine) {
    this.refine = refine
  }

  // Read the instance flag live (it's not state, so it can't be passed as a
  // plain prop). react-autosuggest calls onSuggestionsClearRequested on the blur
  // triggered when a quick-action modal steals focus — we must keep the live
  // query in that case so the dropdown shows results (not the empty/prefill
  // state) behind the modal.
  getAwaitingQuickActionModal() {
    return this.awaitingQuickActionModal
  }

  // Read live by shouldRenderSuggestions. True only between a confirmed
  // quick-action create and the next genuine focus/type, forcing the dropdown
  // shut despite react-autosuggest's stale internal focus state.
  getDropdownClosedByCreate() {
    return this.dropdownClosedByCreate
  }

  // Fill the input with a recent-search term by dispatching a native input
  // event rather than setState. This routes through react-autosuggest's own
  // onChange (method 'type'), which resets its internal highlight index and
  // keeps the list open — so the next ArrowDown reliably lands on the first
  // result row. (setState alone leaves autosuggest's highlight stale and
  // ArrowDown does nothing.)
  fillRecentSearch(query) {
    if (typeof document === 'undefined') return
    const input = document.getElementById('topNavSearchBar')
    if (!input) {
      // Fallback: no DOM input available — fill via state + refine directly.
      this.setState({ value: query })
      if (this.refine) this.refine(query)
      return
    }
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    )
    if (setter && setter.set) {
      setter.set.call(input, query)
    } else {
      input.value = query
    }
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  onKeyDown(e) {
    // While an IME (e.g. Japanese) is composing, Enter confirms the candidate
    // (kanji) rather than submitting — bail so it doesn't run the search. The
    // commit keydown reports keyCode 229 / nativeEvent.isComposing.
    if (e.keyCode === 229 || (e.nativeEvent && e.nativeEvent.isComposing)) return
    // Escape: react-autosuggest's own handler (which runs first) closes the
    // suggestions list; we additionally drop focus so one Esc both closes the
    // dropdown and de-focuses the input.
    if (e.key === 'Escape' || e.keyCode === 27) {
      const input = document.getElementById('topNavSearchBar')
      if (input && typeof input.blur === 'function') input.blur()
      return
    }
    // react-autosuggest fires onSuggestionSelected (method='enter') BEFORE this
    // handler, but ONLY when it has a real internal highlight. When it does,
    // that path handles the Enter (open / preview / results) and sets the flag
    // below, so we bail. Otherwise (input focused, no real selection — even if a
    // stale hover preview is still showing) we go to the full results page.
    //
    // NB: we must not gate on `this.state.highlightedSuggestion` here — that is
    // the debounced *preview* highlight which is intentionally kept after the
    // pointer leaves a row, so it stays truthy when react-autosuggest has no
    // actual selection. Using it would make Cmd+Enter a no-op from the input.
    if (e.key !== 'Enter' && e.keyCode !== 13) return
    if (this.enterHandledBySuggestion) {
      this.enterHandledBySuggestion = false
      return
    }
    this.goToResultsPageForCurrentQuery()
  }

  // Flag setter handed to SearchAutocomplete so its react-autosuggest
  // onSuggestionSelected can mark that an Enter was consumed by a real
  // highlighted row (see onKeyDown).
  markEnterHandled() {
    this.enterHandledBySuggestion = true
  }

  openHighlightedSuggestion(suggestion, openInNewTab) {
    if (!suggestion) return
    // "See all results" → go to the full results page via goToResultsPageForQuery
    // so the top-nav input is blurred. Otherwise it keeps focus and ArrowDown on
    // the results page re-opens this dropdown instead of moving through results.
    if (suggestion.showMore) {
      trackContentUse({
        source: 'searchDropdown',
        query: suggestion.showMore,
        action: 'seeAllResults',
        itemType: 'query',
      })
      this.goToResultsPageForQuery(suggestion.showMore)
      return
    }
    const query = this.state.value
    // Quick actions that open an in-app modal (New Pack / New Class) keep the
    // dropdown open behind the modal — showing the live search results, exactly
    // like the preview modal. The query/results are preserved (onBlur and the
    // suggestions-clear are both short-circuited while awaiting) so Esc/cancel
    // leaves the dropdown as-is; only a confirmed create tears it down
    // (onQuickActionCreated).
    const isModalQuickAction = !!suggestion.__quickAction &&
      (suggestion.action === 'newRepo' || suggestion.action === 'newSection')
    if (isModalQuickAction) {
      // Mark synchronously here (not in the deferred dispatchQuickAction): on a
      // mouse click react-autosuggest blurs the input immediately after this
      // selection (focusInputOnSuggestionClick is false), and onBlur must already
      // see the flag to preserve the query.
      this.awaitingQuickActionModal = true
      this.awaitingQuickActionModalType = suggestion.action === 'newRepo' ?
        'NEW_REPO_MODAL' :
        'NEW_SECTION_MODAL'
      // Fresh launch — clear any stale suppress flag from a prior create so the
      // Esc/cancel restore isn't accidentally swallowed.
      this.suppressQuickActionRestore = false
    } else {
      // Every other terminal branch below opens/navigates/launches something, so
      // close the dropdown and drop focus from the input. Without this the input
      // keeps focus and react-autosuggest re-reveals the dropdown — either
      // immediately (open a class/folder) or when a launched modal later closes.
      this.dismissDropdown()
    }

    // Record content searches only — opening a library item, a recent item, or
    // the "see all results" footer. Navigation / external / quick-action / help
    // selections are not content searches and are skipped.
    const isContentSelection = !!(
      suggestion.__recent ||
      suggestion.showMore ||
      suggestion.createdBy ||
      suggestion.questions ||
      suggestion.ancestors ||
      suggestion.choices
    )
    if (isContentSelection) recordRecentSearch(query)

    if (suggestion.__quickAction) {
      // Defer the dispatch to the next task so a modal launched by the action
      // (e.g. "New Pack") registers its window-level keydown listener AFTER the
      // current Enter keydown has finished bubbling. Otherwise that same Enter
      // reaches the freshly-mounted modal and immediately confirms it (creating
      // a "New Pack" with default values).
      const { action } = suggestion
      trackQuickActionUse({
        source: 'searchDropdown', query, action, label: suggestion.label,
      })
      window.setTimeout(() => this.dispatchQuickAction(action), 0)
      return
    }
    if (suggestion.__helpShowMore) {
      trackHelpUse({
        source: 'searchDropdown', query, kind: 'helpSearch', label: 'Search Help Center',
      })
      window.open(buildHelpSearchUrl(suggestion.query), '_blank', 'noopener,noreferrer')
      return
    }
    if (suggestion.__external) {
      trackHelpUse({
        source: 'searchDropdown',
        query,
        kind: 'externalLink',
        label: suggestion.label,
        url: suggestion.url,
      })
      window.open(suggestion.url, '_blank', 'noopener,noreferrer')
      return
    }
    if (suggestion.__nav) {
      // "Now Playing" is a nav entry without a route — it opens the Live View
      // modal instead of navigating.
      trackNavigationUse({
        source: 'searchDropdown',
        query,
        label: suggestion.label,
        path: suggestion.path,
        action: suggestion.action,
        icon: suggestion.icon,
      })
      if (suggestion.action === 'nowPlaying') {
        showLiveViewModal()
        return
      }
      // Opening a class counts as a content search — record the query so it
      // shows up in recent searches (other nav targets aren't recorded).
      if (suggestion.icon === 'class') recordRecentSearch(query)
      setLastLocation('search')
      if (openInNewTab) {
        window.open(suggestion.path, '_blank', 'noopener,noreferrer')
      } else {
        this.props.history.push(suggestion.path)
      }
      return
    }
    if (suggestion.__recent) {
      const { item, __recentItemType: kind } = suggestion
      if (item) {
        trackContentUse({
          source: 'searchDropdown', query, action: 'open', itemType: kind, itemId: item.id,
        })
        setLastLocation('search')
        const path = kind === 'set' ? `/set/${item.id}` : `/question/${item.id}`
        if (openInNewTab) {
          window.open(path, '_blank', 'noopener,noreferrer')
        } else {
          this.props.history.push(path)
        }
      }
      return
    }
    const itemId = suggestion.id || suggestion._id
    let itemPath
    let itemType
    if (suggestion.createdBy) {
      itemPath = getLinkForRepo(suggestion)
      itemType = 'repo'
    } else if (suggestion.questions) {
      itemPath = `/set/${itemId}`
      itemType = 'set'
    } else if (suggestion.ancestors) {
      itemPath = `/library/${itemId}`
      itemType = 'folder'
      if (suggestion.repo) {
        const parentRepo = getRepoforId(suggestion.repo)
        itemPath = `${getLinkForRepo(parentRepo)}/${itemId}`
      }
    } else {
      itemPath = `/question/${itemId}`
      itemType = 'question'
    }
    trackContentUse({
      source: 'searchDropdown', query, action: 'open', itemType, itemId,
    })
    setLastLocation('search')
    if (openInNewTab) {
      window.open(itemPath, '_blank', 'noopener,noreferrer')
    } else {
      this.props.history.push(itemPath)
    }
  }

  // Close the dropdown and de-focus the search input. Used by every terminal
  // "open" action so the still-focused input doesn't make react-autosuggest
  // re-reveal the dropdown after navigation / after a launched modal closes.
  dismissDropdown() {
    this.currentHighlight = null
    this.shortcutHintKey = null
    this.setState({ value: '', highlightedSuggestion: null, shortcutHintSuggestion: null })
    if (typeof document === 'undefined') return
    const input = document.getElementById('topNavSearchBar')
    if (input && typeof input.blur === 'function') input.blur()
  }

  goToResultsPageForCurrentQuery() {
    this.goToResultsPageForQuery(this.state.value || '')
  }

  goToResultsPageForQuery(rawQuery) {
    const query = rawQuery || ''
    // Opening the results page for a query is a content search — record it.
    recordRecentSearch(query)
    const search = query ? `?${qs.stringify({ query })}` : ''
    this.props.history.push(`/searchresults/${search}`)
    this.setState({ value: '', highlightedSuggestion: null })
    // Drop focus from the search input so the results page (which listens
    // for ArrowUp/ArrowDown/Enter on document) can take keyboard control
    // immediately — the global handler ignores events whose target is an
    // editable field, so leaving focus on the input would swallow them.
    if (
      typeof document !== 'undefined' &&
      document.activeElement &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur()
    }
  }

  getSuggestionValue(suggestion) {
    // Recent-search rows fill the input with their query; every other row keeps
    // the user's typed text (selection opens/navigates rather than filling).
    if (suggestion && suggestion.__recentSearch) return suggestion.query
    return this.state.value
  }

  onChange(event, { newValue, method }) {
    // Arrow navigation (method 'up'/'down') also fires onChange with the
    // highlighted suggestion's getSuggestionValue. We never want arrowing to
    // rewrite the query text (it would, e.g., turn the empty state into a live
    // query when landing on a recent-search row), so ignore those methods.
    if (method === 'up' || method === 'down') return
    // Clear the highlighted suggestion when the user edits the query text so a
    // stale preview from a previous query doesn't linger and Enter reliably
    // goes to the full results page.
    const next = { value: newValue }
    if (method === 'type') {
      // Typing re-engages the dropdown after a post-create close.
      this.dropdownClosedByCreate = false
      next.highlightedSuggestion = null
      // Editing the query drops the highlight, so Enter reliably goes to the
      // full results page rather than acting on a now-stale highlighted row.
      // Also reverts the input hint from "Submit ⌘Enter" back to a bare Enter.
      next.hasActiveHighlight = false
      next.shortcutHintSuggestion = null
      this.shortcutHintKey = null
      this.currentHighlight = null
      // Typing means the user isn't mid quick-action-modal flow; clear any
      // pending flag left by a mousedown that never became a click (drag-off).
      this.awaitingQuickActionModal = false
      this.awaitingQuickActionModalType = null
    }
    this.setState(next)
  }

  onFocus() {
    // A genuine focus clears the post-create close gate so the dropdown behaves
    // normally again (only our own suppressed restore could refocus, so this
    // never fires from the modal teardown).
    this.dropdownClosedByCreate = false
    // Refresh the recent-search snapshot on focus so newly recorded queries
    // (from this or another tab) appear in the empty-state dropdown.
    this.setState({ recentSearches: getRecentSearches(), isInputFocused: true })
  }

  onRecentSearchSelected(query) {
    // Cmd/Ctrl+Enter on a recent search runs the query and navigates to the
    // full results page.
    this.goToResultsPageForQuery(query)
  }

  onRecentSearchFill(query) {
    // Plain Enter / click on a recent search fills the input with the term so
    // the dropdown shows live results for it. Filling via a native input event
    // (see fillRecentSearch) resets autosuggest's highlight so ArrowDown then
    // moves to the first result row.
    this.fillRecentSearch(query)
  }

  onDeleteRecentSearch(query) {
    // Remove a recent search (X button) and refresh the snapshot so the row
    // disappears immediately.
    this.setState({ recentSearches: removeRecentSearch(query) })
  }

  onBlur(event) {
    // While a quick-action modal launched from the dropdown is open, keep the
    // query so it can be restored if the modal is dismissed (the modal steals
    // focus, which would otherwise wipe it here).
    if (this.awaitingQuickActionModal) return
    // A tab/window switch blurs the input but leaves it as document.activeElement.
    // In that case keep the typed query + highlight so nothing is lost on return.
    if (event && event.target && document.activeElement === event.target) return
    // Drop the highlight on blur so re-focusing the input and pressing Enter
    // goes to the full results page rather than re-opening a stale highlight.
    this.currentHighlight = null
    this.shortcutHintKey = null
    this.setState({
      value: '',
      highlightedSuggestion: null,
      shortcutHintSuggestion: null,
      hasActiveHighlight: false,
      isInputFocused: false,
    })
  }

  onSuggestionsClearRequested() {
    this.setState({})
  }

  // Synchronously track the truly-highlighted row (including the "see all
  // results" footer) so the Enter capture-handler always acts on the current
  // row, then update the preview pane via the debounced setter.
  trackHighlight({ suggestion }) {
    // Drive the input hint off the live highlight state (arrow nav / hover):
    // a highlighted row flips the hint to "Submit ⌘Enter", no highlight shows a
    // bare Enter. Updated even on unhighlight (suggestion null), unlike the
    // preview state below which intentionally lingers.
    const nextHasHighlight = !!suggestion
    if (nextHasHighlight !== this.state.hasActiveHighlight) {
      this.setState({ hasActiveHighlight: nextHasHighlight })
    }
    // Ignore unhighlight events (cursor moved off the suggestions list, e.g.
    // onto the preview pane) — keep the last real row so the preview and the
    // pending Enter action stay stable.
    if (!suggestion) return
    this.currentHighlight = suggestion
    // Drive the bottom shortcut hint off the true highlighted row — including
    // the "See all content results" / "Search Help Center" footers, which the
    // preview-pane highlight below intentionally skips. Guard on a stable key
    // (not object identity) because the footer row is recreated every render
    // with no objectID; a reference check would setState endlessly.
    const hintKey = shortcutHintKeyFor(suggestion)
    if (hintKey !== this.shortcutHintKey) {
      this.shortcutHintKey = hintKey
      this.setState({ shortcutHintSuggestion: suggestion })
    }
    this.setPreviewHighlight({ suggestion })
  }

  // Debounced preview-pane highlight. Ignores the "see all results" footer so
  // the last real row's preview stays mounted while the footer is highlighted.
  setPreviewHighlight({ suggestion }) {
    if (suggestion && suggestion.showMore) return
    if (!suggestion) return
    if (suggestion === this.state.highlightedSuggestion) return
    this.setState({ highlightedSuggestion: suggestion })
  }

  // Fired by a quick-action modal on a confirmed create (success). Tears down
  // the preserved dropdown — the inverse of the Esc/cancel path, which restores
  // it (see componentDidUpdate).
  onQuickActionCreated() {
    this.awaitingQuickActionModal = false
    this.awaitingQuickActionModalType = null
    // A confirmed create. New Class's HIDE_MODAL (dispatched inside the
    // newSection action) fires BEFORE this callback, so componentDidUpdate may
    // have already scheduled a deferred restore treating it as a cancel. This
    // flag (checked in that deferred task, which runs after this microtask)
    // suppresses the reopen so the dropdown stays closed on success.
    this.suppressQuickActionRestore = true
    // Force the dropdown shut. react-autosuggest still thinks the input is focused
    // (its blur was swallowed while alwaysRenderSuggestions held the list open
    // behind the modal), so on the modal close it would otherwise keep rendering
    // the list. This gate (read by shouldRenderSuggestions) overrides that until
    // the next genuine focus/type. Reset in onFocus / onChange.
    this.dropdownClosedByCreate = true
    // Clear the live query as well so there's nothing to show even once the gate
    // lifts (while awaiting we deliberately preserved it for the Esc/cancel case).
    if (this.refine) this.refine('')
    this.dismissDropdown()
  }

  // Dispatcher for quick-action selections. Centralised so the registry's
  // `action` token is the single source of truth — add a new quick action by
  // appending to STATIC_QUICK_ACTIONS and adding one arm here.
  dispatchQuickAction(action) {
    switch (action) {
      case 'newSet':
        newSet()
        return true
      case 'newRepo':
        // awaitingQuickActionModal* is set synchronously in
        // openHighlightedSuggestion (needed before the mouse-click blur).
        if (this.props.showNewRepoModal) this.props.showNewRepoModal(this.onQuickActionCreated)
        return true
      case 'newSection':
        if (this.props.showNewSectionModal) this.props.showNewSectionModal(this.onQuickActionCreated)
        return true
      case 'upgradePro':
        // Hash-driven modal handled by UpgradeContainer (same trigger the
        // RepoInvitationBanner / account upgrade buttons use).
        window.location.hash = 'upgrade'
        return true
      default:
        return false
    }
  }

  onSuggestionSelected(event, { suggestion, method }) {
    // react-autosuggest fires this BEFORE our onKeyDown, and only when a
    // suggestion is highlighted. Branch on modifier keys carried on the event:
    //   Ctrl/Cmd+Enter → full results page (all results)
    //   plain Enter      → preview modal (set/question) — intercepted earlier in
    //                     the capture handler, so plain Enter only reaches here
    //                     for non-previewable rows (nav/external/quick-action/
    //                     pack/folder/help/see-all), where it opens/navigates.
    if (method === 'enter') {
      // Ignore the Enter that confirms an IME composition (kanji selection) so
      // it doesn't act on the highlighted row / navigate.
      if (event.keyCode === 229 || (event.nativeEvent && event.nativeEvent.isComposing)) return
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        // Cmd/Ctrl+Enter ("Submit") always searches the entered string and
        // opens the full results page — never the highlighted row the user is
        // standing on. The only exception is the empty-state recent ITEM rows
        // (no typed query), where it falls back to the item's name so the
        // shortcut still does something useful.
        const typed = (this.state.value || '').trim()
        if (!typed && suggestion && suggestion.__recent && suggestion.item) {
          const q = suggestion.__recentItemType === 'set' ?
            suggestion.item.name :
            getQuestionBodyLabel(suggestion.item)
          this.goToResultsPageForQuery(q)
          return
        }
        this.goToResultsPageForCurrentQuery()
        return
      }
      // Shift+Enter is no longer a shortcut — ignore it so it doesn't open the
      // item (previewable rows open the preview on plain Enter; the preview's
      // own Enter then opens the item).
      if (event.shiftKey) {
        event.preventDefault()
        return
      }
      // Plain Enter on a non-previewable row → open / navigate. (Plain Enter on
      // a previewable row is intercepted earlier in handleCaptureKeyDown.)
      this.openHighlightedSuggestion(suggestion, false)
      return
    }

    // Mouse click path (method === 'click') — open / run the item.
    // NB: previewable rows (set/question/recent set-question) handle their own
    // clicks in the DOM (see previewClickHandlers) and stop propagation, so this
    // branch only runs for non-previewable rows (packs/folders/nav/external/
    // quick-action/help/see-all), which always open/execute on click.
    this.openHighlightedSuggestion(suggestion, false)
  }

  // Single click on a previewable row → open the lightweight preview modal.
  // Deferred so a double click (→ open the item) can cancel it first.
  onSuggestionPreviewClick(suggestion) {
    const previewId = getPreviewItemIdForSuggestion(suggestion)
    if (!previewId) {
      this.openHighlightedSuggestion(suggestion, false)
      return
    }
    if (this.suggestionClickTimer) clearTimeout(this.suggestionClickTimer)
    this.suggestionClickTimer = window.setTimeout(() => {
      this.suggestionClickTimer = null
      // Opening a preview is a content search — record the query. We keep the
      // dropdown open (no dismissDropdown) so it stays behind the preview modal
      // and reappears when the modal closes (item 9).
      recordRecentSearch(this.state.value)
      this.props.updatePreviewItem(previewId, 'searchDropdown')
    }, SUGGESTION_CLICK_DELAY)
  }

  // Mouse-click on a quick-action row. The row stops its click from reaching
  // react-autosuggest (which would collapse the dropdown on selection), so we
  // run the action ourselves — same flag/dismiss bookkeeping as the keyboard
  // path so a modal-opening action keeps the dropdown alive behind the modal.
  onQuickActionClick(suggestion) {
    this.openHighlightedSuggestion(suggestion, false)
  }

  // Mousedown on a quick-action row — fires before the click and before the
  // blur react-autosuggest triggers around the selection. For a modal-opening
  // quick action (New Pack / New Class) we set the preserve-the-dropdown flag
  // here so onBlur keeps the query alive; the click then opens the modal via
  // onQuickActionClick → openHighlightedSuggestion (which sets it again,
  // harmlessly). The keyboard path sets the same flag before the modal opens.
  onQuickActionMouseDown(suggestion) {
    if (!suggestion || !suggestion.__quickAction) return
    if (suggestion.action !== 'newRepo' && suggestion.action !== 'newSection') return
    this.awaitingQuickActionModal = true
    this.awaitingQuickActionModalType = suggestion.action === 'newRepo' ?
      'NEW_REPO_MODAL' :
      'NEW_SECTION_MODAL'
    // Fresh launch — clear any stale suppress flag from a prior create.
    this.suppressQuickActionRestore = false
  }

  // Double click on a previewable row → cancel the pending preview and open the
  // item directly.
  onSuggestionPreviewDoubleClick(suggestion) {
    if (this.suggestionClickTimer) {
      clearTimeout(this.suggestionClickTimer)
      this.suggestionClickTimer = null
    }
    this.openHighlightedSuggestion(suggestion, false)
  }

  render() {
    const {
      value, highlightedSuggestion, recentSearches, hasActiveHighlight, isInputFocused,
      shortcutHintSuggestion,
    } = this.state
    const {
      recentActivities, sets, questions, currentModal,
    } = this.props
    const inputProps = {
      placeholder: 'Search',
      value,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onKeyDown: this.onKeyDown,
    }

    const recentSuggestions = buildRecentSuggestions(recentActivities, sets, questions, 5)

    // Keep the dropdown rendered behind a quick-action modal (New Pack / New
    // Class) that was launched FROM the dropdown, so it stays visible with the
    // live search results — matching the preview modal. Guarded on the flag so
    // the same modals opened elsewhere (e.g. Library buttons) don't pop the
    // search dropdown open.
    const keepOpenBehindModal = this.awaitingQuickActionModal && (
      currentModal === 'NEW_REPO_MODAL' || currentModal === 'NEW_SECTION_MODAL'
    )

    return (
      <SearchProviderContainer isTopNav>
        <SearchAutocomplete
          keepOpenBehindModal={keepOpenBehindModal}
          getAwaitingQuickActionModal={this.getAwaitingQuickActionModal}
          getDropdownClosedByCreate={this.getDropdownClosedByCreate}
          getSuggestionValue={this.getSuggestionValue}
          inputProps={inputProps}
          onSuggestionSelected={this.onSuggestionSelected}
          onRecentSearchSelected={this.onRecentSearchSelected}
          onRecentSearchFill={this.onRecentSearchFill}
          onDeleteRecentSearch={this.onDeleteRecentSearch}
          onSuggestionPreviewClick={this.onSuggestionPreviewClick}
          onSuggestionPreviewDoubleClick={this.onSuggestionPreviewDoubleClick}
          onQuickActionClick={this.onQuickActionClick}
          onQuickActionMouseDown={this.onQuickActionMouseDown}
          markEnterHandled={this.markEnterHandled}
          registerRefine={this.registerRefine}
          value={value}
          onSuggestionHighlighted={this.trackHighlight}
          recentSearches={recentSearches}
          recentSuggestions={recentSuggestions}
          highlightedSuggestion={highlightedSuggestion}
          shortcutHintSuggestion={shortcutHintSuggestion}
          hasActiveHighlight={hasActiveHighlight}
          isInputFocused={isInputFocused}
        />
      </SearchProviderContainer>
    )
  }
}

function mapStateToProps(state) {
  return {
    recentActivities: state.recentActivities,
    sets: state.sets,
    questions: state.questions,
    previewItem: state.previewItem,
    currentModal: state.modals.currentModal,
  }
}

const mapDispatchToProps = {
  showNewRepoModal,
  showNewSectionModal,
  updatePreviewItem,
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchBar))
